import React, { useCallback, useState, useMemo } from 'react'
import Modal from 'core/elements/modal/Modal'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import Button from 'core/elements/button/Button'
import Text from 'core/elements/Text'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import ExternalLink from 'core/components/ExternalLink'
import AccordionProgressList from 'core/components/accordion/AccordionProgressList'
import { keys } from 'ramda'
import { allTenantsSelectorByID } from 'app/plugins/account/components/userManagement/tenants/selectors'
import { listTenants } from 'app/plugins/account/components/userManagement/tenants/new-actions'
import useListAction from 'core/hooks/useListAction'
import { useSelector } from 'react-redux'
import SimpleLink from 'core/components/SimpleLink'
import { routeManager } from 'core/utils/routes'
import { IVmDetailsPageTabs } from 'openstack/components/vms/vm-details/model'
import clsx from 'clsx'
import { simpleAddVmTag, simpleMigrateVirtualMachine } from 'openstack/components/vms/actions'
import { listImages } from 'openstack/components/images/actions'
import { vtpmImagesSelector } from 'openstack/components/images/selectors'
import Tooltip from 'core/elements/tooltip'
import {
  VALID_MIGRATION_STATES,
  VALID_COLD_MIGRATION_STATES,
} from 'openstack/components/vms/MigrateVirtualMachineDialog'
import { vtpmMigrationDoc } from 'app/docLinks'

// In-progress migration statuses for both live and cold migrations
// Live migration: accepted -> queued -> preparing -> running -> post-migrating -> completed/failed
// Cold migration: pre-migrating -> migrating -> post-migrating -> finished -> confirming -> confirmed OR error -> reverted
const inProgressStates = [
  'accepted',
  'queued',
  'preparing',
  'running',
  'pre-migrating', // Cold migration initial state
  'migrating', // Cold migration in-progress
  'post-migrating', // Live/cold migration cleanup phase
  'confirming', // Cold migration confirming
]

const useStyles = makeStyles<Theme>((theme) => ({
  noBold: {
    fontWeight: 'normal',
  },
  properties: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
  },
  withIcon: {
    display: 'inline-flex',
    gap: 8,
    alignItems: 'center',
  },
  listHeader: {
    borderRadius: '4px 4px 0px 0px',
    border: `1px solid ${theme.palette.grey[200]}`,
    borderBottom: 'none',
    padding: '12px 16px',
    background: theme.palette.grey[50],
  },
  details: {
    display: 'grid',
    gridTemplateColumns: 'auto auto auto',
    gap: 12,
  },
  extraDetail: {
    marginTop: 24,
    marginBottom: 8,
  },
  detailLink: {
    marginTop: 8,
    textDecoration: 'underline',
  },
  capitalize: {
    textTransform: 'capitalize',
  },
  activeIcon: {
    color: theme.palette.blue[700],
  },
  successIcon: {
    color: theme.palette.green['700-opaque'],
  },
  warningIcon: {
    color: theme.palette.yellow[700],
  },
  errorIcon: {
    color: theme.palette.red[700],
  },
  listIcon: {
    fontSize: 16,
  },
  solidIcon: {
    fontSize: 20,
  },
  retryButton: {
    background: theme.palette.grey[700],
    marginTop: 24,
    '&:hover': {
      background: theme.palette.grey[600],
    },
  },
}))

interface ModalProps {
  host: any
  onClose: () => void
  migrationStatus: {
    status: string
    skippedVms: any[]
    pendingVms: any[]
    completedVms: any[]
    failedVms: any[]
  }
  startTime: string
  endTime: string
  vmTag: string
  refreshResourcesFn: () => void
}

const iconByStatus = {
  pending: 'circle-notch',
  success: 'check',
  skipped: 'exclamation',
  failed: 'x',
}

const getStatusText = (item) => {
  if (item?.migrationStatus === 'success') {
    return 'Completed'
  } else if (item?.migrationStatus === 'skipped') {
    return 'Skipped'
  } else if (item?.migrationStatus === 'failed') {
    return 'Failed'
  } else if (item?.migrationStatus === 'pending') {
    return item?.migration?.status || 'Pending'
  }
}

export default function MigrationProgressModal({
  host,
  onClose,
  migrationStatus,
  startTime,
  endTime,
  vmTag,
  refreshResourcesFn,
}: ModalProps) {
  const [retriedVms, setRetriedVms] = useState({})
  const [failedMigrationVms, setFailedMigrationVms] = useState({})
  const classes = useStyles()
  const allVms = useMemo(() => {
    return [
      ...migrationStatus?.completedVms,
      ...migrationStatus?.pendingVms,
      ...migrationStatus?.failedVms,
      ...migrationStatus?.skippedVms,
    ]
  }, [migrationStatus])
  const overallStatus = migrationStatus?.status

  useListAction(listTenants)
  const tenantsById = useSelector(allTenantsSelectorByID)

  const { loading: loadingImages } = useListAction(listImages)
  const vtpmImages = useSelector(vtpmImagesSelector)

  const isActiveVtpmVm = useCallback(
    (vm) => {
      if (['active', 'paused'].includes(vm['OS-EXT-STS:vm_state'])) {
        // VM is active or paused
        // VM has tpm_version in flavor extra_specs or VM has tpm_version in image
        if (
          vm?.flavor?.extra_specs?.tpm_version ||
          (vm?.image?.id && vtpmImages.some((image) => image?.id === vm?.image?.id))
        ) {
          return true
        }
      }
      return false
    },
    [vtpmImages],
  )

  const retryMigration = useCallback(
    async (item) => {
      // tag VM if the tag does not exist yet on the VM
      try {
        const hasTag = item?.tags?.includes(vmTag)
        if (!hasTag) {
          await simpleAddVmTag({ id: item?.id, tag: vmTag })
        }
      } catch (e) {
        console.log(e)
      }
      // attempt VM migration even if tagging failed
      try {
        const targetHost = item?.migration?.targetNode
        const body = VALID_COLD_MIGRATION_STATES.includes(item['OS-EXT-STS:vm_state'])
          ? {
              migrate: {
                host: targetHost || null,
              },
            }
          : {
              'os-migrateLive': {
                host: targetHost || null,
                block_migration: 'auto',
              },
            }
        await simpleMigrateVirtualMachine({ id: item?.id, body })
        setRetriedVms({
          ...retriedVms,
          [item?.id]: true,
        })
        setFailedMigrationVms({
          ...failedMigrationVms,
          [item?.id]: false,
        })
        // Reload the vm and the migrations, VM may
        // have went from untagged to tagged
        refreshResourcesFn()
      } catch (e) {
        setFailedMigrationVms({
          ...failedMigrationVms,
          [item?.id]: true,
        })
        console.log(e)
      }
    },
    [vmTag, failedMigrationVms, retriedVms],
  )

  const renderStatus = (item) => {
    return (
      <>
        <FontAwesomeIcon
          className={clsx({
            [classes.listIcon]: true,
            [classes.activeIcon]: item?.migrationStatus === 'pending',
            [classes.successIcon]: item?.migrationStatus === 'success',
            [classes.warningIcon]: item?.migrationStatus === 'skipped',
            [classes.errorIcon]: item?.migrationStatus === 'failed',
          })}
          regular
          spin={item?.migrationStatus === 'pending'}
        >
          {iconByStatus[item?.migrationStatus]}
        </FontAwesomeIcon>
        <Text variant="body2" className={classes.capitalize}>
          {getStatusText(item)}
        </Text>
      </>
    )
  }

  const renderName = (item) => {
    return <Text variant="body2">{item?.name}</Text>
  }

  const renderDetails = useCallback(
    (item) => {
      const networks = keys(item?.addresses)
      const ipAddresses = networks.reduce((accum, network) => {
        const addresses = item?.addresses?.[network]
        const ips = addresses.map((address) => address?.addr)
        return [...accum, ips]
      }, [])
      return (
        <>
          <div className={classes.details}>
            <div className={classes.detail}>
              <Text variant="caption1">Tenant</Text>
              <Text variant="body2">{tenantsById[item?.tenant_id]?.name || item?.tenant_id}</Text>
            </div>
            <div className={classes.detail}>
              <Text variant="caption1">IP Address</Text>
              <Text variant="body2">{ipAddresses.join(', ')}</Text>
            </div>
            <div className={classes.detail}>
              <Text variant="caption1">Target Host</Text>
              <Text variant="body2">
                {item?.migration?.dest_node || item?.migration?.dest_compute || 'N/A'}
              </Text>
            </div>
            <div className={classes.detail}>
              <Text variant="caption1">VM State</Text>
              <Text variant="body2" className={classes.capitalize}>
                {item?.['OS-EXT-STS:vm_state']}
              </Text>
            </div>
            <div className={classes.detail}>
              <Text variant="caption1">Start Time</Text>
              <Text variant="body2">{item?.migration?.createdTimeString || 'N/A'}</Text>
            </div>
            <div className={classes.detail}>
              <Text variant="caption1">Completion Time</Text>
              <Text variant="body2">
                {item?.migration && inProgressStates.includes(item?.migration?.status)
                  ? ''
                  : item?.migration?.updatedTimeString || 'N/A'}
              </Text>
            </div>
          </div>
          {item?.migrationStatus === 'skipped' && (
            <div className={classes.extraDetail}>
              <Text variant="caption1">VM Migration Skipped</Text>
              <Text variant="body2">
                This VM's migration was skipped because it was not in a valid state for migration or
                there was an issue triggering the migration when maintenance mode was enabled. A VM
                must be in the Active, Paused, or Stopped state to be migrated.
              </Text>
              {isActiveVtpmVm && (
                <Text variant="body2">
                  <b>NOTE:</b> This is an active vTPM VM. Please ensure they meet the prerequisites
                  listed in <ExternalLink url={vtpmMigrationDoc}>this article</ExternalLink> for
                  live migration.
                </Text>
              )}
              <Tooltip
                message={
                  !VALID_MIGRATION_STATES.includes(item?.['OS-EXT-STS:vm_state'])
                    ? 'This VM must be in the Active, Paused, Suspended, or Stopped state to be migrated'
                    : null
                }
              >
                <Button
                  className={classes.retryButton}
                  icon="arrows-rotate"
                  onClick={() => retryMigration(item)}
                  disabled={!VALID_MIGRATION_STATES.includes(item?.['OS-EXT-STS:vm_state'])}
                >
                  {failedMigrationVms?.[item?.id]
                    ? 'Migration Failed'
                    : !!retriedVms?.[item?.id]
                    ? 'Migration Retried'
                    : 'Retry Migration'}
                </Button>
              </Tooltip>
            </div>
          )}
          {item?.migrationStatus === 'failed' && (
            <div className={classes.extraDetail}>
              <Text variant="caption1">VM Migration Failed</Text>
              <Text variant="body2">
                This VM's migration was triggered and failed. You may check the VM's task list for
                error details. Please make sure that the VM is not a part of any conflicting server
                groups.
              </Text>
              {isActiveVtpmVm && (
                <Text variant="body2">
                  <b>NOTE:</b> This is an active vTPM VM. Please ensure they meet the prerequisites
                  listed in <ExternalLink url={vtpmMigrationDoc}>this article</ExternalLink> for
                  live migration.
                </Text>
              )}
              <div className={classes.detailLink}>
                <SimpleLink
                  src={routeManager.routes.openstack.vmDetails.path({
                    id: item?.id,
                    tab: IVmDetailsPageTabs.Overview,
                  })}
                  bold
                >
                  See Error Details
                </SimpleLink>
              </div>
              <Tooltip
                message={
                  !VALID_MIGRATION_STATES.includes(item?.['OS-EXT-STS:vm_state'])
                    ? 'This VM must be in the Active, Paused, Suspended, or Stopped state to be migrated'
                    : null
                }
              >
                <Button
                  className={classes.retryButton}
                  icon="arrows-rotate"
                  onClick={() => retryMigration(item)}
                  disabled={!VALID_MIGRATION_STATES.includes(item?.['OS-EXT-STS:vm_state'])}
                >
                  {failedMigrationVms?.[item?.id]
                    ? 'Migration Failed'
                    : !!retriedVms?.[item?.id]
                    ? 'Migration Retried'
                    : 'Retry Migration'}
                </Button>
              </Tooltip>
            </div>
          )}
        </>
      )
    },
    [tenantsById, retriedVms, isActiveVtpmVm, failedMigrationVms],
  )

  return (
    <Modal
      title={
        <Text variant="h4">
          View Migration Progress <span className={classes.noBold}>({host?.name})</span>
        </Text>
      }
      onClose={onClose}
      maxWidth={1000}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </>
      }
      open
    >
      <div className={classes.properties}>
        <Text variant="body2">Overall Migration Status</Text>
        {overallStatus === 'pending' && (
          <Text variant="caption1" className={classes.withIcon}>
            <FontAwesomeIcon className={clsx(classes.activeIcon, classes.solidIcon)} solid spin>
              circle-notch
            </FontAwesomeIcon>{' '}
            In progress
          </Text>
        )}
        {overallStatus === 'success' && (
          <Text variant="caption1" className={classes.withIcon}>
            <FontAwesomeIcon className={clsx(classes.successIcon, classes.solidIcon)} solid>
              circle-check
            </FontAwesomeIcon>{' '}
            Completed
          </Text>
        )}
        {overallStatus === 'skipped' && (
          <Text variant="caption1" className={classes.withIcon}>
            <FontAwesomeIcon className={clsx(classes.warningIcon, classes.solidIcon)} solid>
              circle-exclamation
            </FontAwesomeIcon>{' '}
            Warning (Skipped VMs)
          </Text>
        )}
        {overallStatus === 'failed' && (
          <Text variant="caption1" className={classes.withIcon}>
            <FontAwesomeIcon className={clsx(classes.errorIcon, classes.solidIcon)} solid>
              circle-xmark
            </FontAwesomeIcon>{' '}
            Failed
          </Text>
        )}
        <Text variant="body2">Number of VMs Migrated</Text>
        <Text variant="caption1">
          {migrationStatus?.completedVms?.length} of {allVms?.length}
        </Text>
        <Text variant="body2">Start Time</Text>
        <Text variant="caption1">{startTime}</Text>
        <Text variant="body2">Completion Time</Text>
        <Text variant="caption1">{migrationStatus?.pendingVms?.length ? '-' : endTime}</Text>
      </div>
      <div>
        <div className={classes.listHeader}>
          <Text variant="body1">
            <b>Virtual Machines</b> Migration
          </Text>
          <Text variant="body2">
            {migrationStatus?.completedVms?.length} of {allVms?.length} completed
          </Text>
        </div>
        <AccordionProgressList
          items={allVms}
          uniqueIdentifier="id"
          renderStatus={renderStatus}
          renderName={renderName}
          renderDetails={renderDetails}
        />
      </div>
    </Modal>
  )
}
